// Results grid built on ds-grid.
//
// Each program is one block: a program cell spanning its rate rows, with the
// rates laid out in the same column tracks. Blocks are the unit of
// virtualisation, not rows, because a cell cannot span rows that a virtualiser
// positions independently.
//
// ds-grid's GridItem honours gridRow but drops gridColumn, so column placement
// is set inline. Without it a non spanning cell leaves the rest of its row
// empty and the next block falls into a rate column.
import Grid from '@elliemae/ds-grid';
import {
  ArrowheadDown,
  ArrowheadRight,
  CollapseAll,
  ExpandAll,
} from '@elliemae/ds-icons';
import { DSTypography, TYPOGRAPHY_VARIANTS } from '@elliemae/ds-typography';
import { LoanProgram, RateDetails } from '@frontend/pricing-search';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CellContext,
  RateLike,
  ResultsColumn,
  getGridTemplateColumns,
} from './columns';
import { ProgramCell } from './ProgramCell';
import { ProgramNotes } from './ProgramNotes';
import { selectRateWindow } from './rateWindow';
import { useGridPreferences } from './gridPreferences';
import { useVirtualBlocks } from './useVirtualBlocks';
import {
  StyledGridScroll,
  StyledIconButton,
  StyledShowMoreControl,
} from './styled';

export interface ResultsGridProps {
  programs: LoanProgram[];
  columns: ResultsColumn[];
  /** clientSettings maxAlternates. 0 or null shows every rate. */
  ratesToDisplay: number | null;
  expandFirstProgram: boolean;
  relockRequest: { specificProgramID?: number } | null;
  rateRequestStatus?: number;
  isHistoricalPricing: boolean;
  disableLockLinks: boolean;
  loanData: unknown;
  loanId: number;
  displayEstimatedMI: string | undefined;
  miCertification?: unknown;
  proposedMIAmount?: number;
  onRefreshLockDays: (lockDays: string, loanData: unknown) => void;
  onSelectRate: (program: LoanProgram, rate: RateLike) => void;
  onOpenDetails: (program: LoanProgram, rate: RateLike) => void;
  onSelectProgram: (program: LoanProgram) => void;
}

const CELL_BORDER = '1px solid neutral-200';
// pui-theme has no matching token.
const TARGET_ROW_BG = '#EBF6FF';
// Alternating band for the row banding setting.
const BAND_BG = '#F6FBFF';
const ICON_COLOR = '#005181';

// Row wrapper that assistive tech sees but CSS grid ignores. Rows and cells
// carry program and rate ids because only a window of blocks is in the DOM, so
// position is not a stable way to address them.
const AriaRow = ({
  children,
  rowIndex,
  programID,
  rateDataID,
}: {
  children: React.ReactNode;
  rowIndex?: number;
  programID?: number;
  rateDataID?: number;
}) => (
  <div
    role="row"
    aria-rowindex={rowIndex}
    data-program-id={programID}
    data-rate-id={rateDataID}
    style={{ display: 'contents' }}
  >
    {children}
  </div>
);

const ProgramBlock = ({
  program,
  columns,
  ratesToDisplay,
  expanded,
  showAll,
  onToggleExpanded,
  onToggleShowAll,
  rowIndex,
  banded,
  lines,
  ...ctx
}: {
  program: LoanProgram;
  columns: ResultsColumn[];
  ratesToDisplay: number | null;
  expanded: boolean;
  showAll: boolean;
  onToggleExpanded: () => void;
  onToggleShowAll: () => void;
  rowIndex: number;
  banded: boolean;
  lines: { vertical: boolean; horizontal: boolean };
} & Omit<
  ResultsGridProps,
  'programs' | 'columns' | 'ratesToDisplay' | 'expandFirstProgram'
>) => {
  const rates = (program.rateDetails ?? []) as RateDetails[];
  const targetRateDataID = (program as unknown as RateLike).rateDataID;
  const targetIdx = rates.findIndex((r) => r.rateDataID === targetRateDataID);

  const rateWindow = useMemo(
    () => selectRateWindow(rates, targetIdx, showAll ? null : ratesToDisplay),
    [rates, targetIdx, showAll, ratesToDisplay],
  );

  // Collapsed shows the target rate only.
  const visibleRates: RateLike[] = expanded
    ? (rateWindow.rates as unknown as RateLike[])
    : ([rates[targetIdx >= 0 ? targetIdx : 0]].filter(
        Boolean,
      ) as unknown as RateLike[]);

  const showMoreRow = expanded && (!rateWindow.isComplete || showAll);
  const spannedRows = visibleRates.length + (showMoreRow ? 1 : 0);

  const cellCtx: CellContext = {
    programID: program.programID,
    productTypeID: program.productTypeID,
    program: program as unknown as Record<string, unknown>,
    displayEstimatedMI: ctx.displayEstimatedMI,
    relockRequest: ctx.relockRequest,
    rateRequestStatus: ctx.rateRequestStatus,
    miCertification: ctx.miCertification,
    proposedMIAmount: ctx.proposedMIAmount,
    onSelectRate: (rate) => ctx.onSelectRate(program, rate),
    onOpenDetails: (rate) => ctx.onOpenDetails(program, rate),
  };

  // Banding keys off the row index rather than a CSS nth-child rule: only part
  // of the list is in the DOM, so nth-child would reassign bands while scrolling.
  const blockBg = banded && rowIndex % 2 === 0 ? BAND_BG : undefined;

  // Banding marks the program, so it stays on the program cell and on the one
  // rate row that represents it. Inside an expanded stack the alternate rates
  // stay plain, otherwise the band competes with the target rate highlight.
  const rateCellBg = (isTarget: boolean) => {
    if (isTarget && expanded) return TARGET_ROW_BG;
    return expanded ? undefined : blockBg;
  };
  const Arrowhead = expanded ? ArrowheadDown : ArrowheadRight;
  const ShowMoreIcon = showAll ? CollapseAll : ExpandAll;

  return (
    <>
      {visibleRates.map((rate, i) => {
        const isTarget = rate.rateDataID === targetRateDataID;
        return (
          <AriaRow
            key={rate.rateDataID}
            rowIndex={i === 0 ? rowIndex : undefined}
            programID={program.programID}
            rateDataID={rate.rateDataID}
          >
            {i === 0 && (
              <Grid
                role="gridcell"
                aria-colindex={1}
                aria-rowspan={spannedRows}
                style={{ gridColumn: 1 }}
                gridRow={`span ${spannedRows}`}
                data-testid="program-cell"
                data-program-id={program.programID}
                rows="auto"
                alignContent="start"
                gutter="xxs"
                p="xxs"
                pr="16px"
                borderBottom={lines.horizontal ? CELL_BORDER : undefined}
                borderRight={lines.vertical ? CELL_BORDER : undefined}
                bg={blockBg}
              >
                {/* Expander centres against the name and date block, not the
                    whole spanned cell, so it stays put when notes expand. */}
                <Grid cols={['auto', '1fr']} gutter="xxs" alignItems="center">
                  <StyledIconButton
                    type="button"
                    aria-expanded={expanded}
                    // Only reference the id when that row is actually
                    // rendered, otherwise the relationship dangles.
                    aria-controls={
                      showMoreRow ? `stack-${program.programID}` : undefined
                    }
                    aria-label={expanded ? 'Collapse rates' : 'Expand rates'}
                    data-testid="expander"
                    data-program-id={program.programID}
                    onClick={() => {
                      onToggleExpanded();
                      ctx.onSelectProgram(program);
                    }}
                  >
                    <Arrowhead size="m" color={ICON_COLOR} />
                  </StyledIconButton>
                  <ProgramCell
                    program={program}
                    relockRequest={ctx.relockRequest}
                    rateRequestStatus={ctx.rateRequestStatus}
                    isHistoricalPricing={ctx.isHistoricalPricing}
                    expanded={expanded}
                    disableLockLinks={ctx.disableLockLinks}
                    loanData={ctx.loanData}
                    onRefreshLockDays={ctx.onRefreshLockDays}
                  />
                </Grid>
                {expanded && (
                  <ProgramNotes
                    program={program as never}
                    loanId={ctx.loanId}
                  />
                )}
              </Grid>
            )}
            {columns.map((col, ci) => (
              <Grid
                key={col.id}
                role="gridcell"
                aria-colindex={ci + 2}
                style={{ gridColumn: ci + 2 }}
                data-testid={`cell-${col.id}`}
                data-program-id={program.programID}
                data-rate-id={rate.rateDataID}
                data-target={isTarget || undefined}
                tabIndex={-1}
                alignItems="center"
                // grid items default to min-width auto, so content wider than
                // the track escapes into the next column instead of wrapping
                minWidth={0}
                px="xxs"
                minHeight="32px"
                borderBottom={lines.horizontal ? CELL_BORDER : undefined}
                borderRight={
                  lines.vertical && ci < columns.length - 1
                    ? CELL_BORDER
                    : undefined
                }
                bg={rateCellBg(isTarget)}
                fontWeight={isTarget && expanded ? 600 : undefined}
              >
                {col.renderCell(rate, cellCtx)}
              </Grid>
            ))}
          </AriaRow>
        );
      })}

      {showMoreRow && (
        <AriaRow>
          <Grid
            role="gridcell"
            aria-colindex={2}
            id={`stack-${program.programID}`}
            style={{ gridColumn: `2 / span ${columns.length}` }}
            alignItems="center"
            minHeight="28px"
            borderBottom={lines.horizontal ? CELL_BORDER : undefined}
            bg={TARGET_ROW_BG}
          >
            <StyledShowMoreControl
              type="button"
              data-testid="show-more"
              data-program-id={program.programID}
              onClick={onToggleShowAll}
            >
              <ShowMoreIcon size="s" color={ICON_COLOR} />
              {showAll ? 'Show Less...' : 'Show More...'}
            </StyledShowMoreControl>
          </Grid>
        </AriaRow>
      )}
    </>
  );
};

export const ResultsGrid = ({
  programs,
  columns,
  ratesToDisplay,
  expandFirstProgram,
  ...ctx
}: ResultsGridProps) => {
  const template = useMemo(() => getGridTemplateColumns(columns), [columns]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const keys = useMemo(() => programs.map((p) => p.programID), [programs]);

  // Expansion and Show More live here, not in ProgramBlock. Windowing unmounts
  // offscreen blocks, which would discard that state, and the block would then
  // re-measure at its collapsed height and shift everything below it while
  // scrollTop stayed put.
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [showAllIds, setShowAllIds] = useState<Set<number>>(new Set());

  const toggleIn = (
    setIds: React.Dispatch<React.SetStateAction<Set<number>>>,
    id: number,
  ) =>
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const toggleExpanded = useCallback(
    (id: number) => toggleIn(setExpandedIds, id),
    [],
  );
  const toggleShowAll = useCallback(
    (id: number) => toggleIn(setShowAllIds, id),
    [],
  );

  // A new search or a filter change gives a different set of programs, and
  // expansion is not carried across it. Reseeds the first program at the same
  // time, so the client setting still applies to whatever now sorts first.
  const programSignature = keys.join(',');
  const appliedSignature = useRef<string | null>(null);
  useEffect(() => {
    if (appliedSignature.current === programSignature) return;
    appliedSignature.current = programSignature;
    const firstProgramId = keys[0];
    setExpandedIds(
      new Set(
        expandFirstProgram && firstProgramId != null ? [firstProgramId] : [],
      ),
    );
    setShowAllIds(new Set());
  }, [programSignature, keys, expandFirstProgram]);
  const { startIndex, endIndex, topSpacer, bottomSpacer, measureBlock } =
    useVirtualBlocks(scrollRef, keys);
  const { preferences } = useGridPreferences();
  const lines = useMemo(
    () => ({
      vertical: preferences.verticalLines,
      horizontal: preferences.horizontalLines,
    }),
    [preferences.verticalLines, preferences.horizontalLines],
  );
  const visible = programs.slice(startIndex, endIndex + 1);
  const visibleKey = visible.map((p) => p.programID).join(',');

  // ds-grid does not forward refs, so blocks are measured from the container by
  // their data-program-id rather than by holding a ref per block.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return undefined;
    // Must match the spanning program cell only. Rows, rate cells and the
    // controls also carry data-program-id, and measuring one of those would
    // report a 32px row as the whole block height.
    const cells = Array.from(
      root.querySelectorAll<HTMLElement>('[data-testid="program-cell"]'),
    );
    const report = (el: HTMLElement) =>
      measureBlock(Number(el.dataset.programId), el.offsetHeight);
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => report(entry.target as HTMLElement));
    });
    cells.forEach((cell) => {
      report(cell);
      observer.observe(cell);
    });
    return () => observer.disconnect();
  }, [visibleKey, measureBlock]);

  return (
    <StyledGridScroll ref={scrollRef} data-testid="results-scroll">
      <Grid
        role="grid"
        aria-label="Available Programs"
        aria-colcount={columns.length + 1}
        aria-rowcount={programs.length}
        data-testid="results-grid"
        cols={[template]}
        borderTop={lines.horizontal ? CELL_BORDER : undefined}
        borderBottom={lines.horizontal ? CELL_BORDER : undefined}
        borderLeft={lines.vertical ? CELL_BORDER : undefined}
        borderRight={lines.vertical ? CELL_BORDER : undefined}
      >
        <AriaRow>
          {['Program Name', ...columns.map((c) => c.header)].map(
            (header, i) => (
              <Grid
                key={header}
                role="columnheader"
                aria-colindex={i + 1}
                style={{ gridColumn: i + 1 }}
                justifyContent={i === 0 ? 'flex-start' : 'flex-end'}
                alignItems="center"
                px="xxs"
                py="xxxs"
                borderBottom="1px solid neutral-300"
              >
                <DSTypography
                  variant={TYPOGRAPHY_VARIANTS.B1}
                  fontSize="12px"
                  fontWeight={600}
                  color="#25292f"
                >
                  {header.toUpperCase()}
                </DSTypography>
              </Grid>
            ),
          )}
        </AriaRow>

        {topSpacer > 0 && (
          <div
            aria-hidden
            data-testid="top-spacer"
            style={{
              gridColumn: `1 / span ${columns.length + 1}`,
              height: topSpacer,
            }}
          />
        )}

        {visible.map((program, i) => (
          <ProgramBlock
            key={program.programID}
            program={program}
            columns={columns}
            ratesToDisplay={ratesToDisplay}
            expanded={expandedIds.has(program.programID)}
            showAll={showAllIds.has(program.programID)}
            onToggleExpanded={() => toggleExpanded(program.programID)}
            onToggleShowAll={() => toggleShowAll(program.programID)}
            rowIndex={startIndex + i + 1}
            banded={preferences.rowBanding}
            lines={lines}
            {...ctx}
          />
        ))}

        {bottomSpacer > 0 && (
          <div
            aria-hidden
            data-testid="bottom-spacer"
            style={{
              gridColumn: `1 / span ${columns.length + 1}`,
              height: bottomSpacer,
            }}
          />
        )}
      </Grid>
    </StyledGridScroll>
  );
};
